import React, { useCallback, useLayoutEffect, useRef } from 'react';

interface InfiniteScrollProps {
  disabled?: boolean;
  loading: 'initial' | 'more' | 'reload' | 'refresh' | false;
  hasMore: boolean;
  children?: React.ReactNode;
  loader?: React.ReactNode;
  alwaysShowLoader?: boolean;
  next: () => void;
}

export function InfiniteScroll(props: InfiniteScrollProps) {
  const {
    disabled,
    loading,
    hasMore,
    children,
    loader,
    alwaysShowLoader,
    next
  } = props;

  const visible = useRef(false);
  const handleAnchorVisible = useRef<() => void>(undefined as never);
  handleAnchorVisible.current = () => {
    if (loading || !hasMore) return;
    next();
  };

  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      visible.current = entries[0] && entries[0].isIntersecting;
      if (visible.current) {
        handleAnchorVisible.current();
      }
    },
    []
  );

  const observer = useRef<IntersectionObserver>(
    new IntersectionObserver(handleIntersection)
  );

  const anchorRef = useRef<HTMLElement | null>();
  const setAnchorRef = useCallback((el: HTMLElement | null) => {
    if (anchorRef.current) {
      observer.current.unobserve(anchorRef.current);
    }
    anchorRef.current = el;
    if (anchorRef.current) {
      observer.current.observe(anchorRef.current);
    }
  }, []);

  useLayoutEffect(() => {
    let animationFrameId = requestAnimationFrame(() => {
      animationFrameId = requestAnimationFrame(() => {
        const records = observer.current.takeRecords();
        if (records.length) {
          handleIntersection(records);
        } else if (visible.current) {
          handleAnchorVisible.current();
        }
      });
    });
    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  });

  const showLoader =
    alwaysShowLoader || loading === 'initial' || loading === 'more';

  const anchor = (
    <div
      className="infinite-scroll-anchor"
      ref={setAnchorRef}
      style={{ width: '100%', height: '1px' }}
    >
      {showLoader && loader}
    </div>
  );

  return (
    <>
      {children}
      {!disabled && anchor}
    </>
  );
}
